//
// Typeahead Controller
// --------------------
// This controller is responsible for handling the typeahead functionality
//
// Typeahead is different than Searchbox in that it doesn't rely on a form submission
// A solitary input can be used to trigger the typeahead

import { Controller } from "@hotwired/stimulus";
import Combobox from "@github/combobox-nav";
import debounce from "lodash/debounce";
import { get } from "@rails/request.js";

const MIN_LENGTH_VALUE = 3;

export default class extends Controller {
  static values = {
    url: String,
  };
  static targets = ["container", "hint", "input", "list"];

  initialize() {
    this.handleInput = debounce(this.handleInput, 200);
  }

  connect() {
    if (this.inputTarget.value !== null) {
      // Fetch results
      this.fetchResults();
    }
  }

  // Wedge this in here for now
  // TODO: Move this to a separate controller
  revealHints() {
    this.hintTargets.forEach((hint) => {
      // set hint opacity to 1
      hint.style.opacity = "1";
    });
  }

  disconnect() {
    this.combobox?.destroy();
  }

  start() {
    this.combobox?.destroy();
    this.combobox = new Combobox(this.inputTarget, this.listTarget);
    this.combobox.start();
  }

  stop() {
    this.combobox?.stop();
    this.listTarget.innerHTML = null;
  }

  // Handles the typeahead input
  handleInput(event) {
    this.fetchResults();
  }

  async fetchResults() {
    const query = this.inputTarget.value.trim();
    if (!query || query.length < MIN_LENGTH_VALUE) {
      this.stop();
      return;
    }

    if (!this.hasUrlValue) return;

    const response = await get(this.urlValue, {
      contentType: "application/json",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      query: { q: query },
      responseKind: "json",
    });

    if (response.ok) {
      const data = await response.json;
      this.listTarget.innerHTML = renderResults(data, query);
      const hasResults = !!this.listTarget.querySelector('[role="option"]');
      if (hasResults) {
        this.start();
      } else {
        this.listTarget.innerHTML = renderEmptyResults();
        this.stop();
      }
    }
  }
}

function renderEmptyResults() {
  return `<li class="list-group-item">No results found</li>`;
}

function renderResults(data, query) {
  const results = data.map((result) => {
    const linkTo = `/start?church_id=${result?.slug}`;
    return `
      <li class="list-group-item p-0">
        <a role="option" href="${linkTo}" class="list-group-item-action">
          <div class="list-group-item-content d-flex px-4 py-2">
            <div class="list-group-item-body">
              <span class="fw-semibold">${result.name}</span>
              <span class="fs-7 text-muted">${result.city},${result.state}</span>
              <div class="fs-7 text-muted">${result?.address1?.length > 0 ? result.address1 : "-"}</div>
            </div>
          </div>
        </a>
      </li>`;
  });
  results.push(`
    <li class="list-group-item p-0">
      <a role="option" href="/start?church_name=${query}" class="list-group-item-action">
        <div class="list-group-item-content d-flex px-4 py-2">
          <div class="list-group-item-body text-center w-100">
            Or, add a new church named "${query}"...
          </div>
        </div>
      </a>
  </li>`);
  return results.join("");
}

function handleCommit(event) {
  const firstLink = event.target.querySelector("a");
  if (firstLink) {
    firstLink.click();
  }
}
